import { useEffect, useSyncExternalStore } from "react";
import { Link } from "react-router-dom";

export interface ToastAction {
  label: string;
  href: string;
}

export interface ToastInput {
  title: string;
  body: string;
  action?: ToastAction;
}

interface ToastRecord extends ToastInput {
  id: string;
}

let toasts: ToastRecord[] = [];
const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return toasts;
}

function createToastId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function dismissToast(id: string) {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  toasts = toasts.filter((toast) => toast.id !== id);
  emit();
}

export function showToast(input: ToastInput): string {
  const id = createToastId();
  toasts = [...toasts, { id, ...input }];
  emit();
  timers.set(
    id,
    setTimeout(() => {
      dismissToast(id);
    }, 6000),
  );
  return id;
}

export function ToastViewport() {
  const activeToasts = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => () => {
    for (const id of [...timers.keys()]) dismissToast(id);
  }, []);

  if (activeToasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-3 px-4">
      {activeToasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className="pointer-events-auto rounded-xl border border-emerald-200 bg-white p-4 shadow-lg"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">{toast.title}</p>
              <p className="mt-1 text-sm text-slate-600">{toast.body}</p>
              {toast.action && (
                <Link
                  to={toast.action.href}
                  onClick={() => dismissToast(toast.id)}
                  className="mt-2 inline-flex text-sm font-medium text-blue-700 underline"
                >
                  {toast.action.label}
                </Link>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismissToast(toast.id)}
              className="text-sm text-slate-500 hover:text-slate-700"
              aria-label="Dismiss notification"
            >
              Dismiss
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
